/* eslint-disable import/default */
import { type FC, type JSX } from 'react';

import editorWorker from 'monaco-editor/esm/vs/editor/editor.worker?worker';
import cssWorker from 'monaco-editor/esm/vs/language/css/css.worker?worker';
import htmlWorker from 'monaco-editor/esm/vs/language/html/html.worker?worker';
import jsonWorker from 'monaco-editor/esm/vs/language/json/json.worker?worker';
import tsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker?worker';
/* eslint-enable import/default */

import { Toastify, TooltipService } from 'pixel-react';
import { Helmet } from 'react-helmet-async';
import { Routes, Route } from 'react-router-dom';

import { Home } from '@/pages/Home';
import { LogIn } from '@/pages/Login';

import 'pixel-react/lib/styles.css';
import styles from './App.module.scss';
import BodyLoaderGuard from './components/BodyLoader';
import ImportLockBridge from './components/ImportLockBridge/ImportLockBridge';
import { ProtectedRoute } from './components/ProtectedRoute/ProtectedRoute';
import { PublicRoute } from './components/PublicRoute/PublicRoute';
import { AuthProvider } from './context/Auth/AuthProvider'; // Import AuthProvider
import signUpRoutes from './routes';

import './lib/service/privilegeService';

declare global {
  interface Window {
    MonacoEnvironment: any;
  }
}
// 2. Map the workers to the global Monaco environment
window.MonacoEnvironment = {
  getWorker(_workerId: string, label: string) {
    if (label === 'json') {
      return new jsonWorker();
    }
    if (label === 'css' || label === 'scss' || label === 'less') {
      return new cssWorker();
    }
    if (label === 'html' || label === 'handlebars' || label === 'razor') {
      return new htmlWorker();
    }
    if (label === 'typescript' || label === 'javascript') {
      return new tsWorker();
    }
    return new editorWorker(); // Fallback for plain text/other languages
  },
};

const App: FC = (): JSX.Element => {
  const details = import.meta.env.VITE_CLIENT_DETAIL;

  return (
    <>
      <Helmet>
        <link rel="icon" type="image/x-icon" href={details?.favIconUrl} />
        <title>AI Scriptless Automation Testing Tool | {details?.name}</title>
      </Helmet>
      <AuthProvider>
        <ImportLockBridge />
        <BodyLoaderGuard />
        <Toastify />
        <TooltipService />
        <div className={`${styles.app}`}>
          <Routes>
            {/* Public Routes */}
            <Route element={<PublicRoute />}>
              <Route path="/*" element={<LogIn />} />

              {signUpRoutes.map(({ path, element }) => (
                <Route key={path} path={path} element={element} />
              ))}
            </Route>

            {/* Protected Routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/app/*" element={<Home />} />
            </Route>
          </Routes>
        </div>
      </AuthProvider>
    </>
  );
};

export default App;
